'use client'

import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useLanguage } from '@/lib/i18n/language-provider'

interface RenameProjectDialogProps {
  open: boolean
  currentName: string
  onClose: () => void
  onSubmit: (newName: string) => Promise<void>
}

export function RenameProjectDialog({
  open,
  currentName,
  onClose,
  onSubmit
}: RenameProjectDialogProps) {
  const { t } = useLanguage()
  const [newName, setNewName] = useState(currentName)
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // Mettre à jour le nom quand le dialog s'ouvre
  useEffect(() => {
    if (open) {
      setNewName(currentName)
    }
  }, [open, currentName])
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!newName.trim() || newName === currentName) {
      onClose()
      return
    }
    
    setIsSubmitting(true)
    try {
      await onSubmit(newName.trim())
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("synoptic.dialog.renameProject")}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="project-name">{t("synoptic.dialog.projectName")}</Label>
              <Input
                id="project-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={t("synoptic.dialog.projectNamePlaceholder")}
                autoFocus
                maxLength={100}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t("synoptic.dialog.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting || !newName.trim()}>
              {isSubmitting ? t("synoptic.dialog.renaming") : t("synoptic.dialog.rename")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

